import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaRegBell,
    FaEye,
    FaSearch,
    FaInbox,
    FaBullhorn,
    FaHistory,
    FaTimes,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaTimesCircle,
    FaTrashAlt,
    FaCheckDouble
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import './JobSeekerNotifications.css';

const JobSeekerNotifications = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'messages', 'history'
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching notifications", err);
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = notifications.filter(n => n._id !== id);
            setNotifications(updated);

            // Sync with header count
            const newUnread = updated.filter(n => !n.isRead).length;
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
        } catch (err) {
            console.error("Mark as read error", err);
        }
    };

    const handleNotificationClick = async (notif) => {
        // Mark as read first
        await markAsRead(notif._id);

        const title = notif.title.toLowerCase();
        const msg = notif.message.toLowerCase();

        // 1. Check for Global/Broadcast first (No redirection as per user request)
        if (title.includes('global') || title.includes('announcement') || title.includes('broadcast') ||
            msg.includes('global') || msg.includes('announcement')) {
            return;
        }

        // Redirection logic
        if (title.includes('job') || msg.includes('job') || title.includes('application') || msg.includes('application')) {
            navigate('/jobseeker-dashboard/applications');
        } else if (title.includes('profile') || msg.includes('profile') || title.includes('resume') || msg.includes('resume')) {
            navigate('/jobseeker-dashboard/profile');
        } else if (title.includes('support') || msg.includes('ticket') || title.includes('help')) {
            navigate('/jobseeker-dashboard/support');
        } else if (title.includes('interview') || msg.includes('interview')) {
            navigate('/jobseeker-dashboard/applications'); // Interviews are often tracked in applications/tracker
        } else if (title.includes('board') || msg.includes('opening')) {
            navigate('/jobseeker-dashboard/job-board');
        }
    };
    const markAllRead = async () => {
        try {
            showLoading("Clearing all notifications...");
            const token = localStorage.getItem('token');
            await axios.patch('/api/notifications/mark-all-read', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);

            // Sync with header count
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: 0 } }));

            closeLoading();
            showSuccess("Success", "All notifications dismissed.");
        } catch (err) {
            closeLoading();
            showError("Action Failed", "Could not clear hub.");
        }
    };

    const filteredNotifications = notifications.filter(n => {
        const msg = n.message || '';
        const title = n.title || '';
        const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'alerts') return matchesSearch && (n.type === 'Alert' || n.target === 'All' || n.target === 'job_seekers');
        if (activeTab === 'messages') return matchesSearch && (n.type === 'Info' || n.type === 'Success');
        return matchesSearch;
    });

    const getIcon = (type) => {
        switch (type) {
            case 'Alert': return <FaExclamationTriangle className="js-type-icon js-alert" />;
            case 'Success': return <FaCheckCircle className="js-type-icon js-success" />;
            case 'Warning': return <FaTimesCircle className="js-type-icon js-warning" />;
            default: return <FaInfoCircle className="js-type-icon js-info" />;
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="jobseeker-notifications js-fade-in">
            <div className="js-hub-header">
                <div className="js-header-info">
                    <h1 className="js-hub-title"><FaRegBell /> Communication Hub</h1>
                    <p className="js-hub-subtitle">Centralized updates, job alerts, and recruiter communications.</p>
                </div>
                <div className="js-header-actions">
                    <button className="js-clear-all-btn" onClick={markAllRead} disabled={notifications.length === 0}>
                        <FaCheckDouble /> Mark All Read
                    </button>
                </div>
            </div>

            <div className="js-hub-controls">
                <div className="js-tab-filters">
                    <button
                        className={`js-tab-btn ${activeTab === 'alerts' ? 'js-active' : ''}`}
                        onClick={() => setActiveTab('alerts')}
                    >
                        <FaBullhorn /> Job Alerts
                        {notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'job_seekers')).length > 0 && (
                            <span className="js-tab-badge">{notifications.filter(n => (n.type === 'Alert' || n.target === 'All' || n.target === 'job_seekers')).length}</span>
                        )}
                    </button>
                    <button
                        className={`js-tab-btn ${activeTab === 'messages' ? 'js-active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        <FaInbox /> Corporate Inbox
                        {notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length > 0 && (
                            <span className="js-tab-badge">{notifications.filter(n => (n.type === 'Info' || n.type === 'Success')).length}</span>
                        )}
                    </button>
                    <button
                        className={`js-tab-btn ${activeTab === 'history' ? 'js-active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FaHistory /> Archived Updates
                    </button>
                </div>

                <div className="js-search-bar">
                    <FaSearch className="js-search-icon" />
                    <input
                        type="text"
                        placeholder="Filter communications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="js-notifications-list">
                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notif) => (
                        <div
                            key={notif._id}
                            className={`js-notif-card js-${notif.type.toLowerCase()}`}
                            onClick={() => handleNotificationClick(notif)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="js-notif-icon-box">
                                {getIcon(notif.type)}
                            </div>
                            <div className="js-notif-content">
                                <div className="js-notif-meta">
                                    <span className={`js-notif-badge js-${notif.type.toLowerCase()}`}>{notif.type}</span>
                                    <span className="js-notif-date">{new Date(notif.createdAt).toLocaleString()}</span>
                                </div>
                                <h3 className="js-notif-title">{notif.title}</h3>
                                <p className="js-notif-message">{notif.message}</p>
                            </div>
                            <button className="js-dismiss-btn" onClick={() => markAsRead(notif._id)} title="Dismiss">
                                <FaTimes />
                            </button>
                        </div>
                    ))
                ) : (
                    <div className="js-empty-hub">
                        <div className="js-empty-icon-wrapper">
                            <FaRegBell />
                        </div>
                        <h3>Operational Hub Clear</h3>
                        <p>No new communications detected in this channel.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobSeekerNotifications;
